import React from 'react';
import { Link } from 'react-router-dom';
import { CpuChipIcon, CogIcon, LightBulbIcon, ServerStackIcon, ArrowRightIcon, RocketLaunchIcon, ShareIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';

const PageHeader = ({ title, subtitle }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.1s'}}>{subtitle}</p>}
    </div>
  </div>
);

const SolutionHighlight = ({ icon: Icon, title, description, features, linkTo, imageUrl, animationDelay }) => (
  <div className="bg-figma-dark-bg-secondary border border-figma-dark-border rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col group hover:animate-subtle-3d-tilt-hover animate-fade-in-up" style={{ animationDelay }}>
    {imageUrl && (
      <div className="w-full h-60 bg-figma-dark-bg-tertiary overflow-hidden">
        <img src={imageUrl} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
      </div>
    )}
    <div className="p-6 flex flex-col flex-grow">
      <div className="flex items-start mb-4">
        { Icon && <Icon className="h-8 w-8 text-figma-dark-primary mr-4 mt-1 flex-shrink-0" /> }
        <h2 className="text-xl font-semibold text-figma-dark-text-primary group-hover:text-figma-dark-primary transition-colors">{title}</h2>
      </div>
      <p className="text-base text-figma-dark-text-secondary leading-relaxed mb-5 flex-grow">
        {description}
      </p>
      {features && features.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold text-figma-dark-text-primary mb-2">Key Capabilities:</h4>
          <ul className="space-y-1.5">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start text-sm text-figma-dark-text-secondary">
                <ArrowRightIcon className="h-4 w-4 text-figma-dark-primary mr-2 mt-0.5 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkTo && (
        <Link to={linkTo} className="text-sm font-semibold text-figma-dark-primary hover:text-figma-dark-primary-hover inline-flex items-center mt-auto group-hover:underline">
          Explore Solution <ArrowRightIcon className="ml-1.5 h-4 w-4" />
        </Link>
      )}
    </div>
  </div>
);

const SolutionsPage = () => {
  const solutions = [
    {
      icon: RocketLaunchIcon,
      title: 'AI-Powered Innovation Platform',
      description: 'A comprehensive suite of tools and services to develop, deploy, and manage AI applications that integrate seamlessly with your workflows, driving intelligent automation and advanced analytics.',
      features: [
        'Custom Machine Learning Model Development & Deployment',
        'Natural Language Processing (NLP) & Understanding (NLU)',
        'Advanced Computer Vision & Image Analysis Solutions',
        'Predictive Maintenance & Anomaly Detection Systems'
      ],
      linkTo: '/solutions/ai-development',
      imageUrl: 'https://placehold.co/600x400/7F5AF0/16161A?text=AI+Innovation+Suite&font=montserrat' // Figma primary
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Intelligent Automation Systems',
      description: 'Custom-designed robotic and software automation solutions for manufacturing, logistics, and complex operational processes, enhancing precision, speed, and safety.',
      features: [
        'Industrial Robot & Cobot Integration Specialists',
        'Autonomous Mobile Robots (AMRs) for Smart Logistics',
        'Robotic Process Automation (RPA) for Business Efficiency',
        'AI-Enhanced Robotic Control & Perception Systems'
      ],
      linkTo: '/solutions/robotics',
      imageUrl: 'https://placehold.co/600x400/2CB67D/16161A?text=Integrated+Automation&font=montserrat' // Figma accent
    },
    {
      icon: ShareIcon,
      title: 'Cloud-Native & DevOps Excellence',
      description: 'Architect and implement scalable, secure cloud infrastructure and modern DevOps practices to accelerate your digital transformation and enhance operational agility.',
      features: [
        'Multi-Cloud Strategy & Migration (AWS, Azure, GCP)',
        'Serverless Architectures & Microservices',
        'Continuous Integration & Delivery (CI/CD) Pipelines',
        'Robust Cloud Security & Governance Frameworks'
      ],
      linkTo: '/services/cloud-solutions',
      imageUrl: 'https://placehold.co/600x400/242630/EFEFF1?text=DevOps+CI/CD+Flow&font=montserrat' // Figma bg-secondary
    },
    {
      icon: LightBulbIcon,
      title: 'Bespoke Digital Products',
      description: 'Engineer high-performance custom software applications, from innovative SaaS products to enterprise-grade systems, tailored to your unique business requirements.',
      features: [
        'Full-Stack Web & Progressive Web App (PWA) Development',
        'Native & Cross-Platform Mobile Application Design',
        'Scalable API Design & Microservices Architecture',
        'User-Centric UX/UI Design & Rapid Prototyping'
      ],
      linkTo: '/services/software-development',
      imageUrl: 'https://placehold.co/600x400/16161A/94A3B8?text=Digital+Product+Studio&font=montserrat' // Figma bg-primary
    },
  ];

  return (
    <div className="bg-figma-dark-bg-primary min-h-screen">
      <PageHeader 
        title="Our Solutions"
        subtitle="Discover our portfolio of advanced AI, Robotics, and Software solutions, meticulously engineered to address complex challenges and unlock new opportunities for growth and efficiency in your business."
      />
      
      <section className="py-12 md:py-16 bg-figma-dark-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg shadow-2xl overflow-hidden animate-fade-in-up">
            <img 
              src="https://placehold.co/1200x450/16161A/7F5AF0?text=Transformative+Technology+Solutions&font=montserrat"
              alt="Panoramic showcase of various innovative technology solutions in a dark theme"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-figma-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
            <h2 className="text-3xl font-bold text-figma-dark-text-primary">Tailored Technology Solutions</h2>
            <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-2xl mx-auto">
              Driving business transformation through intelligent design, robust engineering, and strategic implementation.
            </p>
          </div>
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-10">
            {solutions.map((solution, index) => (
                <SolutionHighlight 
                  key={solution.title}
                  icon={solution.icon} 
                  title={solution.title} 
                  description={solution.description}
                  features={solution.features}
                  linkTo={solution.linkTo}
                  imageUrl={solution.imageUrl}
                  animationDelay={`${index * 0.1}s`}
                />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Figma Dark Style */}
      <section className="bg-figma-dark-primary py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let's Build Your Next Solution
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            We adapt our core technologies to meet the specific demands of various sectors, delivering impactful results where they matter most. Discuss your project with our experts today.
          </p>
          <Link
            to="/contact"
            className="bg-figma-dark-bg-secondary hover:bg-figma-dark-bg-tertiary text-figma-dark-text-primary font-semibold px-10 py-3.5 rounded-lg text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage; 